import { useMemo, useState } from 'react';
import { ASSET_CLASSES, BROKERS, INSTRUMENTS, SPREAD_USE_CASES } from '@/mocks/compare';
import type { AssetClass, Instrument } from '@/mocks/compare';
import { fmtSpread, spreadCost, spreadFor } from '../calculations';
import { CompareBar, NumberField, SectionHeading, Segmented, SelectField, Tag } from './ui';

type ClassFilter = AssetClass | 'all';
type SortKey = 'symbol' | 'spread';

export default function SpreadSection() {
  const [assetClass, setAssetClass] = useState<ClassFilter>('all');
  const [sort, setSort] = useState<SortKey>('symbol');
  const [costSymbol, setCostSymbol] = useState('EURUSD');
  const [lot, setLot] = useState(1);

  const instruments = useMemo(() => {
    let list = INSTRUMENTS.filter((i) => assetClass === 'all' || i.assetClass === assetClass);
    if (sort === 'spread') {
      list = [...list].sort((a, b) => a.refSpread - b.refSpread);
    }
    return list;
  }, [assetClass, sort]);

  const costInst = INSTRUMENTS.find((i) => i.symbol === costSymbol) ?? INSTRUMENTS[0];
  const costList = BROKERS.map((b) => ({ broker: b, cost: spreadCost(costInst, b, lot) }));
  const minCost = Math.min(...costList.map((c) => c.cost));

  return (
    <section id="spread" className="scroll-mt-24 rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      <SectionHeading
        eyebrow="So sánh Spread"
        title="So sánh Spread Forex/CFD"
        subtitle="Đối chiếu mức spread của nhiều nhà môi giới cho cùng một sản phẩm, và quy đổi thành chi phí USD thực tế cho một lệnh."
      />

      {/* Filters */}
      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="w-full lg:max-w-xl">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Loại tài sản</span>
          <Segmented<ClassFilter> options={ASSET_CLASSES} value={assetClass} onChange={setAssetClass} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500">Sắp xếp:</span>
          <Segmented<SortKey>
            options={[
              { value: 'symbol', label: 'Theo ký hiệu' },
              { value: 'spread', label: 'Spread thấp nhất' },
            ]}
            value={sort}
            onChange={setSort}
          />
        </div>
      </div>

      {/* Spread matrix table */}
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200/80">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="bg-slate-50 text-left">
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Sản phẩm
              </th>
              {BROKERS.map((b) => (
                <th key={b.id} className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="flex h-6 w-6 items-center justify-center rounded-md font-display text-[10px] font-bold text-white"
                      style={{ backgroundColor: b.color }}
                    >
                      {b.initial}
                    </span>
                    <span className="text-xs font-bold text-slate-700">{b.name}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {instruments.map((inst) => {
              const spreads = BROKERS.map((b) => spreadFor(inst, b));
              const min = Math.min(...spreads);
              return (
                <tr key={inst.symbol} className="border-t border-slate-100 transition-colors hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold text-slate-800">{inst.symbol}</span>
                      <Tag tone={inst.assetClass === 'Forex' ? 'violet' : 'amber'}>{inst.category}</Tag>
                    </div>
                  </td>
                  {spreads.map((s, idx) => {
                    const cheapest = s === min;
                    return (
                      <td key={BROKERS[idx].id} className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 font-mono text-xs font-bold tabular-nums ${
                            cheapest ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'text-slate-600'
                          }`}
                        >
                          {fmtSpread(s, inst.spreadUnit)}
                          {cheapest && <i className="ri-check-line text-[11px]" />}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-slate-400">
        Ô màu xanh là mức spread thấp nhất cho sản phẩm đó. Dữ liệu mang tính tham khảo, spread thực tế biến động theo thời gian thực.
      </p>

      {/* Spread cost calculator */}
      <div className="mt-8 rounded-2xl border border-slate-200/80 bg-slate-50/50 p-5">
        <h3 className="font-display text-lg font-bold text-slate-900">Chi phí Spread cho một lệnh</h3>
        <p className="mt-1 text-xs text-slate-500">
          Chọn sản phẩm và khối lượng để quy đổi spread thành chi phí USD thực tế.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="Sản phẩm"
            value={costSymbol}
            onChange={setCostSymbol}
            options={INSTRUMENTS.map((i) => ({ value: i.symbol, label: `${i.symbol} — ${i.name}` }))}
          />
          <NumberField label="Khối lượng (lot)" value={lot} onChange={setLot} step={0.01} suffix="lot" />
        </div>
        <div className="mt-4 space-y-1.5">
          {costList.map(({ broker, cost }) => (
            <CompareBar
              key={broker.id}
              label={broker.name}
              value={cost}
              display={`${cost.toFixed(2)} USD`}
              max={Math.max(...costList.map((c) => c.cost))}
              color={broker.color}
              highlight={cost === minCost}
            />
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-400">
          Chi phí spread chưa bao gồm commission. Tài khoản Raw có spread thấp nhưng thu commission cố định.
        </p>
      </div>

      {/* Use cases */}
      <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-3">
        {SPREAD_USE_CASES.map((u) => (
          <div key={u.title} className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
              <i className={u.icon} />
            </span>
            <h4 className="mt-3 text-sm font-bold text-slate-800">{u.title}</h4>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">{u.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}